/**
 * Cache offline (doc 09 §3.4 / §1.6): lista de conversas e fila de envio em
 * IndexedDB via idb-keyval. A fila nunca e descartada por expurgo (RF-UX-055);
 * pendentes sobrevivem ao fechar o app (EC-UX-001).
 */
import { clear, createStore, del, get, keys, set } from "idb-keyval";
import type { Conversation, Message } from "@zapir/shared";

const store = createStore("zapir", "offline");

const CONV_KEY = (tenantId: string) => `conversations:${tenantId}`;
const MSG_KEY = (conversationId: string) => `messages:${conversationId}`;
const QUEUE_KEY = (tenantId: string) => `send-queue:${tenantId}`;
const DRAFT_KEY = (conversationId: string) => `draft:${conversationId}`;

export interface CachedConversations {
  items: Conversation[];
  cachedAt: number;
}

export async function cacheConversations(
  tenantId: string,
  items: Conversation[],
): Promise<void> {
  try {
    await set(CONV_KEY(tenantId), { items, cachedAt: Date.now() } satisfies CachedConversations, store);
  } catch {
    /* IndexedDB indisponivel (modo privado) — degrada para memoria */
  }
}

export async function readConversations(tenantId: string): Promise<CachedConversations | null> {
  try {
    return (await get<CachedConversations>(CONV_KEY(tenantId), store)) ?? null;
  } catch {
    return null;
  }
}

export async function cacheMessages(conversationId: string, items: Message[]): Promise<void> {
  try {
    await set(MSG_KEY(conversationId), { items, cachedAt: Date.now() }, store);
  } catch {
    /* noop */
  }
}

export async function readMessages(conversationId: string): Promise<Message[] | null> {
  try {
    const cached = await get<{ items: Message[] }>(MSG_KEY(conversationId), store);
    return cached?.items ?? null;
  } catch {
    return null;
  }
}

/** Item da fila de envio offline (RF-UX-051). */
export interface QueuedMessage {
  clientMessageId: string;
  conversationId: string;
  /** Texto — ou legenda (`caption`) quando há `mediaId`. */
  body: string;
  quotedMessageId?: string;
  isInternalNote: boolean;
  /** IDs de usuarios mencionados (@) em notas internas. */
  mentions?: string[];
  /** Anexo já enviado via `POST /api/media/uploads` (docs/06). */
  mediaId?: string | null;
  /** Kind do anexo (bolha otimista + retry sem re-upload). */
  mediaKind?: "image" | "video" | "audio" | "voice" | "document" | "sticker" | null;
  mediaName?: string;
  mediaSize?: number;
  mediaMime?: string;
  /** Duração medida do áudio/voz em ms (vai no upload `durationMs`). */
  mediaDurationMs?: number | null;
  createdAt: number;
}

export async function enqueue(tenantId: string, item: QueuedMessage): Promise<void> {
  const queue = (await get<QueuedMessage[]>(QUEUE_KEY(tenantId), store)) ?? [];
  await set(QUEUE_KEY(tenantId), [...queue, item], store);
}

export async function readQueue(tenantId: string): Promise<QueuedMessage[]> {
  try {
    return (await get<QueuedMessage[]>(QUEUE_KEY(tenantId), store)) ?? [];
  } catch {
    return [];
  }
}

export async function removeFromQueue(tenantId: string, clientMessageId: string): Promise<void> {
  const queue = await readQueue(tenantId);
  await set(
    QUEUE_KEY(tenantId),
    queue.filter((q) => q.clientMessageId !== clientMessageId),
    store,
  );
  await deleteMediaBlob(clientMessageId);
}

/** Atualiza um item da fila (ex.: grava `mediaId` após o upload). */
export async function updateQueued(
  tenantId: string,
  clientMessageId: string,
  patch: Partial<QueuedMessage>,
): Promise<void> {
  const queue = await readQueue(tenantId);
  await set(
    QUEUE_KEY(tenantId),
    queue.map((q) => (q.clientMessageId === clientMessageId ? { ...q, ...patch } : q)),
    store,
  );
}

/* ---------------- Blobs de mídia da fila offline ---------------- */

/**
 * Binário do anexo pendente (docs/06 + offline-first 09 §1.6): permite
 * escolher a foto sem rede e drenar upload+envio ao reconectar. Chave
 * separada da fila para nunca ser expurgada com o cache de mensagens.
 */
const MEDIA_BLOB_KEY = (clientMessageId: string) => `media-blob:${clientMessageId}`;

export async function saveMediaBlob(clientMessageId: string, blob: Blob): Promise<void> {
  try {
    await set(MEDIA_BLOB_KEY(clientMessageId), blob, store);
  } catch {
    /* IndexedDB indisponível — o envio segue só com o blob em memória */
  }
}

export async function readMediaBlob(clientMessageId: string): Promise<Blob | null> {
  try {
    return (await get<Blob>(MEDIA_BLOB_KEY(clientMessageId), store)) ?? null;
  } catch {
    return null;
  }
}

export async function deleteMediaBlob(clientMessageId: string): Promise<void> {
  try {
    await del(MEDIA_BLOB_KEY(clientMessageId), store);
  } catch {
    /* noop */
  }
}

export async function saveDraft(conversationId: string, text: string): Promise<void> {
  try {
    if (text) await set(DRAFT_KEY(conversationId), text, store);
    else await del(DRAFT_KEY(conversationId), store);
  } catch {
    /* noop */
  }
}

export async function readDraft(conversationId: string): Promise<string> {
  try {
    return (await get<string>(DRAFT_KEY(conversationId), store)) ?? "";
  } catch {
    return "";
  }
}

/** Expurgo de cache corrompido (EC-UX-003): recria o store e rebusca. */
export async function resetOfflineCache(): Promise<void> {
  try {
    await clear(store);
  } catch {
    /* noop */
  }
}

export async function cacheKeys(): Promise<IDBValidKey[]> {
  try {
    return await keys(store);
  } catch {
    return [];
  }
}
